import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable, of} from 'rxjs';

import {ApiLocationService} from '@services/api-location/api-location.service';

import {Machine} from '@models/machine/machine';
import {User} from '@models/user/user';

import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {shareReplay} from 'rxjs/operators';
import {UserPageResponse} from '@models/adminstration/user/user-page-response';
import {USER_ROLE} from '@constants/user-role.constant';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private USER_LIST_URL_SUFFIX = '/api/web/admin/user';
  private USER_LIST_WITH_CURSOR_URL_SUFFIX = '/api/web/admin/user/list/:tenantId/:limit/:cursor';
  private USER_RESTRICTED_INTERACTIONS_URL_SUFFIX = '/api/web/admin/user/restrictedUserInteraction';
  private USER_ADD_URL_SUFFIX = '/api/web/admin/user/';
  private USER_UPDATE_URL_SUFFIX = '/api/web/admin/user/:id';
  private USER_DELETE_URL_SUFFIX = '/api/web/admin/user/:id';
  private USER_RESET_PASSWORD_URL_SUFFIX = '/api/web/admin/user/:id/reset';
  private USER_BAN_URL_SUFFIX = '/api/web/admin/user/:id';
  private USER_EMAIL_UNIQUE_URL_SUFFIX = '/api/web/admin/user/isEmailUnique?email=:email';
  private USER_BAN_ALL_USERS_FOR_ORGANIZATION_URL_SUFFIX = '/api/web/admin/user/ban/:orgId';

  private usersResponseCache = new Map();
  private usersCacheFromElastic = '';
  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }
  /**
   * @deprecated - should use elastic search approach
   */
  public list(): Observable<any> {
    const url: string = this.BASE_URL + this.USER_LIST_URL_SUFFIX;
    const usersFromCache = this.usersResponseCache.get(url);
    if (usersFromCache) {
      return of(usersFromCache);
    }
    console.debug('users list url: ' + url);
    const response = this.http.get<User[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.usersResponseCache.set(url, result);
    });
    return response;
  }
  /**
   * @deprecated - should use elastic search approach
   */
  public listWithCursor(organizationId: number, limit: number, cursor: string): Observable<any> {
    let url: string = this.BASE_URL + this.USER_LIST_WITH_CURSOR_URL_SUFFIX;
    url = url.replace(':tenantId', String(organizationId)).replace(':limit', String(limit)).replace(':cursor', cursor);
    const usersFromCache = this.usersResponseCache.get(url);
    if (usersFromCache) {
      return of(usersFromCache);
    }
    const response = this.http.get<UserPageResponse>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.usersResponseCache.set(url, result);
    });
    return response;
  }

  public restrictedUserInteraction(): Observable<any> {
    const url: string = this.BASE_URL + this.USER_RESTRICTED_INTERACTIONS_URL_SUFFIX;

    return this.http.get<Machine[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public isEmailUnique(userName: string): Observable<any> {
    let url: string = this.BASE_URL + this.USER_EMAIL_UNIQUE_URL_SUFFIX;
    url = url.replace(':email', String(userName));
    return this.http.get<boolean[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public addUser(user: User): Observable<any> {
    const url: string = this.BASE_URL + this.USER_ADD_URL_SUFFIX;
    const request = {
      displayName: user.displayName,
      language: user.language,
      machineGroup: user.machineGroup,
      parentId: user.parentId,
      role: user.role,
      username: user.username,
      whiteListedMachineryGroupsMap: user.whiteListedMachineryGroupsMap
    };
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public updateUser(user: User): Observable<any> {
    let url: string = this.BASE_URL + this.USER_UPDATE_URL_SUFFIX;
    url = url.replace(':id', user.firebaseUid);
    return this.http.post(url, user, HTTPS_REQUEST_OPTIONS);
  }

  public delete(firebaseUserId: string): Observable<any> {
    let url: string = this.BASE_URL + this.USER_DELETE_URL_SUFFIX;
    url = url.replace(':id', firebaseUserId);

    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public resetPassword(user: User): Observable<any> {
    let url: string = this.BASE_URL + this.USER_RESET_PASSWORD_URL_SUFFIX;
    url = url.replace(':id', String(user.firebaseUid));
    return this.http.post(url, user, HTTPS_REQUEST_OPTIONS);
  }

  public banUser(user: User): Observable<any> {
    let url: string = this.BASE_URL + this.USER_BAN_URL_SUFFIX;
    url = url.replace(':id', user.firebaseUid);
    return this.http.post(url, user, HTTPS_REQUEST_OPTIONS);
  }

  public banAllUsersForOrganization(organizationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.USER_BAN_ALL_USERS_FOR_ORGANIZATION_URL_SUFFIX;
    url = url.replace(':orgId', String(organizationId));
    return this.http.post<string[]>(url, organizationId, HTTPS_REQUEST_OPTIONS);
  }

  public getBadgeClassList(): {[key in USER_ROLE]: string} {
    return {
      [USER_ROLE.SUPERADMIN]: 'badge-warning',
      [USER_ROLE.TECH_ADMIN]: 'badge-success',
      [USER_ROLE.ADMIN]: 'badge-success',
      [USER_ROLE.DISTRIBUTOR]: 'badge-purple',
      [USER_ROLE.ANALYTICS]:'badge-info',
      [USER_ROLE.USER]: 'badge-dark',
      [USER_ROLE.VIEWER]: 'badge-dark'
    };
  }
}
